import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PackageCheck, FileSpreadsheet, BellRing } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const RemindersButton = ({ contestId }) => {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.lastReminderRun(contestId).then((r) => setLast(r?.run_at ? r : null));
  }, [contestId]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.sendRemindersNow(contestId);
      setLast(r);
      toast.success(`Promemoria inviati: ${r.sent} (controllati ${r.checked})`);
    } catch (e) {
      toast.error("Invio promemoria non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      {last && (
        <span className="text-xs text-muted-foreground" data-testid="reminders-last-run">
          Ultimo invio: {new Date(last.run_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })} · {last.sent} email
        </span>
      )}
      <Button data-testid="send-reminders-button" variant="secondary" size="sm" onClick={run} disabled={busy} className="gap-2">
        <BellRing className="h-4 w-4" /> {busy ? "Invio…" : "Invia promemoria ora"}
      </Button>
    </div>
  );
};

const MARGIN_V = { left: 20, right: 8 };
const MARGIN_H = { left: 0, right: 8 };
const MARGIN_V30 = { left: 30, right: 8 };
const RADIUS_R = [0, 6, 6, 0];
const CURSOR = { fill: "rgba(255,255,255,0.04)" };
const LEGEND_STYLE = { fontSize: 12 };
const tooltipStyle = {
  contentStyle: { background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#e2e8f0" },
};

export const ChartCard = ({ title, subtitle, children, testId, auto }) => (
  <Card className="p-5 bg-card border-border" data-testid={testId}>
    <h3 className="font-display font-bold text-base">{title}</h3>
    {subtitle && <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>}
    <div className={`${auto ? "" : "h-64"} mt-2`}>{children}</div>
  </Card>
);

export const PlaysWinsBar = ({ data, layout = "horizontal" }) => {
  if (!data?.length) return <Empty />;
  const vertical = layout === "vertical";
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout={vertical ? "vertical" : "horizontal"} margin={vertical ? MARGIN_V : MARGIN_H}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        {vertical ? (
          <>
            <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
            <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={120} />
          </>
        ) : (
          <>
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} interval={0} angle={data.length > 12 ? -45 : 0} textAnchor={data.length > 12 ? "end" : "middle"} height={data.length > 12 ? 50 : 30} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
          </>
        )}
        <Tooltip {...tooltipStyle} cursor={CURSOR} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Bar dataKey="plays" name="Giocate" fill="#3B82F6" radius={3} />
        <Bar dataKey="wins" name="Vincite" fill="#F59E0B" radius={3} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const TrendArea = ({ data }) => {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={MARGIN_H}>
        <defs>
          <linearGradient id="gPlays" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gWins" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
        <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Area type="monotone" dataKey="plays" name="Giocate" stroke="#3B82F6" fill="url(#gPlays)" strokeWidth={2} />
        <Area type="monotone" dataKey="wins" name="Vincite" stroke="#F59E0B" fill="url(#gWins)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const RulesTrendArea = ({ data }) => {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={MARGIN_H}>
        <defs>
          <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.5} /><stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient>
          <linearGradient id="gAcc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
        <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Area type="monotone" dataKey="views" name="Letture" stroke="#8B5CF6" fill="url(#gViews)" strokeWidth={2} />
        <Area type="monotone" dataKey="accepted" name="Accettazioni" stroke="#10B981" fill="url(#gAcc)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const LandingTrendArea = ({ data }) => {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={MARGIN_H}>
        <defs>
          <linearGradient id="gLv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
          <linearGradient id="gLc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.5} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
          <linearGradient id="gLl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
        <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Area type="monotone" dataKey="views" name="Visite" stroke="#3B82F6" fill="url(#gLv)" strokeWidth={2} />
        <Area type="monotone" dataKey="cta" name="Clic Partecipa" stroke="#F59E0B" fill="url(#gLc)" strokeWidth={2} />
        <Area type="monotone" dataKey="login" name="Clic Registrati" stroke="#10B981" fill="url(#gLl)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const LP_SOURCE_LABEL = { codici: "QR cartolina codici", ricevuta: "QR ricevuta", scontrino: "QR scontrino", share: "Invito amici", link: "Link diretto" };
const LP_SOURCE_COLOR = { codici: "#8B5CF6", ricevuta: "#EC4899", scontrino: "#F59E0B", share: "#25D366", link: "#64748b" };

export const LandingSourceBar = ({ data }) => {
  if (!data?.length) return <Empty />;
  const rows = data.map((d) => ({ ...d, label: LP_SOURCE_LABEL[d.source] || d.source }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={MARGIN_V30}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
        <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={120} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Bar dataKey="views" name="Visite" radius={RADIUS_R}>{rows.map((r) => <Cell key={r.source} fill={LP_SOURCE_COLOR[r.source] || "#64748b"} />)}</Bar>
        <Bar dataKey="unique" name="Visitatori unici" fill="#94a3b8" radius={RADIUS_R} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const pct = (a, b) => (b ? `${Math.round((1000 * a) / b) / 10}%` : "—");

export const LandingOverviewCard = ({ contests }) => {
  const rows = (contests || []).filter((c) => c.landing && (c.landing.views || c.landing.cta || c.landing.login));
  return (
    <ChartCard title="Landing page" subtitle="Visite e clic su Partecipa / Registrati per concorso" testId="chart-landing-overview" auto>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground py-4">Nessuna visita registrata finora.</p> : (
        <div className="overflow-auto max-h-64">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Concorso</th><th className="py-1 pr-2 text-right">Visite</th><th className="py-1 pr-2 text-right">Partecipa</th><th className="py-1 pr-2 text-right">Registrati</th><th className="py-1 pr-2 text-right">Inviti</th><th className="py-1 text-right">Conv.</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-border/50" data-testid={`landing-overview-row-${c.id}`}>
                  <td className="py-1.5 pr-2 truncate max-w-[180px]">{c.label}</td>
                  <td className="py-1.5 pr-2 text-right font-mono-code">{c.landing.views}</td>
                  <td className="py-1.5 pr-2 text-right font-mono-code text-amber-400">{c.landing.cta}</td>
                  <td className="py-1.5 pr-2 text-right font-mono-code text-emerald-400">{c.landing.login}</td>
                  <td className="py-1.5 pr-2 text-right font-mono-code text-sky-400">{c.landing.share || 0}</td>
                  <td className="py-1.5 text-right font-mono-code">{pct(c.landing.cta + c.landing.login, c.landing.views)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
};


const SOURCE_LABEL = { totem: "QR totem", tv: "QR display TV", codici: "QR PDF codici", ricevuta: "QR ricevuta", concorsi: "Pagina concorsi", totem_pannello: "Pannello totem", punto_vendita: "Punto vendita", link: "Link diretto", page: "Link diretto" };
const SOURCE_COLOR = { totem: "#F59E0B", tv: "#3B82F6", codici: "#8B5CF6", ricevuta: "#EC4899", concorsi: "#10B981", totem_pannello: "#FBBF24", punto_vendita: "#14B8A6", link: "#64748b", page: "#64748b" };

export const RulesSourceBar = ({ data }) => {
  if (!data?.length) return <Empty />;
  const rows = data.map((d) => ({ ...d, label: SOURCE_LABEL[d.source] || d.source }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={MARGIN_V30}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
        <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={110} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        <Bar dataKey="views" name="Letture" radius={RADIUS_R}>{rows.map((r) => <Cell key={r.source} fill={SOURCE_COLOR[r.source] || "#64748b"} />)}</Bar>
        <Bar dataKey="unique" name="Lettori unici" fill="#94a3b8" radius={RADIUS_R} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const WaDeliveryArea = ({ data }) => {
  if (!data?.length) return <Empty />;
  const series = [["read", "Letti", "#38BDF8"], ["delivered", "Consegnati", "#2DD4BF"], ["sent", "Inviati", "#10B981"], ["failed", "Falliti", "#EF4444"]];
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={MARGIN_H}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
        <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={LEGEND_STYLE} />
        {series.map(([k, n, c]) => <Area key={k} type="monotone" stackId="wa" dataKey={k} name={n} stroke={c} fill={c} fillOpacity={0.35} strokeWidth={2} />)}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const WaGlobalCard = ({ wa }) => {
  if (!wa) return null;
  return (
    <ChartCard title="WhatsApp: consegne e letture (tutti i concorsi)" subtitle={`${wa.total} messaggi · ${wa.rates.delivered}% consegnati · ${wa.rates.read}% letti · ${wa.rates.failed}% falliti${wa.counts.queued ? ` · ${wa.counts.queued} in coda (orario notturno)` : ""}`} testId="chart-wa-global">
      <WaDeliveryArea data={wa.by_day} />
    </ChartCard>
  );
};

export const PrizeBar = ({ data }) => {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={MARGIN_V}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
        <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={130} />
        <Tooltip {...tooltipStyle} cursor={CURSOR} />
        <Bar dataKey="wins" name="Vincite" fill="#059669" radius={3} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const Empty = () => (
  <div className="h-full grid place-items-center text-sm text-muted-foreground">Nessun dato disponibile</div>
);

export const KpiCard = ({ label, value, accent, testId }) => (
  <Card className="p-4 bg-card border-border" data-testid={testId}>
    <div className={`font-display font-black text-2xl ${accent ? "text-amber-400" : ""}`}>{value}</div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
  </Card>
);

export const ChannelCard = ({ data, exportUrl }) => {
  const rows = data.by_channel || [];
  const total = rows.reduce((a, r) => a + r.plays, 0) || 1;
  return (
    <Card className="p-5 bg-card border-border" data-testid="chart-channel">
      <h3 className="font-display font-bold">Giocate per canale</h3>
      <p className="text-xs text-muted-foreground mb-4">Totem vs WhatsApp</p>
      <div className="grid grid-cols-2 gap-3">
        {rows.map((r) => {
          const pct = Math.round((r.plays / total) * 100);
          const wa = r.key === "whatsapp";
          return (
            <div key={r.key} className={`rounded-lg p-4 border ${wa ? "border-emerald-500/30 bg-emerald-500/10" : "border-primary/30 bg-primary/10"}`} data-testid={`channel-stat-${r.key}`}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{r.label}</div>
              <div className="font-display font-black text-3xl mt-1">{r.plays}</div>
              <div className="text-xs text-muted-foreground mt-1">{r.wins} vincite · {pct}% delle giocate</div>
              <div className="h-1.5 rounded-full bg-secondary mt-3 overflow-hidden"><div className={`h-full ${wa ? "bg-emerald-400" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>
              {exportUrl && (
                <a href={`${exportUrl}?channel=${r.key}`} className="text-[11px] underline text-muted-foreground mt-2 inline-block" data-testid={`export-channel-${r.key}`}>Excel solo {r.label}</a>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export const StatsGrid = ({ data, exportUrl, contestId, playsExportUrl }) => {
  const rate = data.total_plays ? Math.round((data.total_wins / data.total_plays) * 100) : 0;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Giocate totali" value={data.total_plays} testId="kpi-plays" />
        <KpiCard label="Vincite totali" value={data.total_wins} accent testId="kpi-wins" />
        <KpiCard label="Tasso di vincita" value={`${rate}%`} testId="kpi-rate" />
        <KpiCard label="Punti vendita" value={data.by_store.length} testId="kpi-stores" />
      </div>
      <div className="mb-5"><ChannelCard data={data} exportUrl={playsExportUrl} /></div>
      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Andamento giornaliero" subtitle="Giocate e vincite per giorno" testId="chart-day">
          <TrendArea data={data.by_day} />
        </ChartCard>
        <ChartCard title="Fasce orarie" subtitle="Distribuzione per ora del giorno (ora italiana)" testId="chart-hour">
          <PlaysWinsBar data={data.by_hour} />
        </ChartCard>
        <ChartCard title="Punti vendita" subtitle="Giocate e vincite per sede" testId="chart-store">
          <PlaysWinsBar data={data.by_store.slice(0, 10)} layout="vertical" />
        </ChartCard>
        <ChartCard title="Premi assegnati" subtitle="Vincite per tipologia di premio" testId="chart-prize">
          <PrizeBar data={data.by_prize.slice(0, 10)} />
        </ChartCard>
        {data.rules && (
          <ChartCard title="Regolamento" subtitle={`Letture e accettazioni per giorno · ${data.rules.unique_views} lettori unici · ${data.rules.accepted} accettazioni`} testId="chart-rules">
            <RulesTrendArea data={data.rules.by_day} />
          </ChartCard>
        )}
        {data.rules && (
          <ChartCard title="Da dove leggono il regolamento" subtitle="Letture per origine: quale materiale funziona meglio" testId="chart-rules-source">
            <RulesSourceBar data={data.rules.by_source} />
          </ChartCard>
        )}
        {data.landing && (
          <ChartCard title="Landing page" subtitle={`Visite e clic per giorno · ${data.landing.unique_views} visitatori unici · ${data.landing.cta} Partecipa (${data.landing.cta_rate}%) · ${data.landing.login} Registrati (${data.landing.login_rate}%)`} testId="chart-landing">
            <LandingTrendArea data={data.landing.by_day} />
          </ChartCard>
        )}
        {data.landing && (
          <ChartCard title="Da dove arrivano alla landing" subtitle={`Visite per origine · ${data.landing.share} condivisioni · Inviti: ${data.landing.referral?.ref_visits || 0} visite da link personale, ${data.landing.referral?.friends_played || 0} amici hanno giocato, ${data.landing.referral?.rewards || 0} premi emessi (${data.landing.referral?.redeemed || 0} usati) · Iscritti dalla landing: ${data.landing.signup?.signups || 0}, premi iscrizione ${data.landing.signup?.rewards || 0} (${data.landing.signup?.rewards_used || 0} usati)`} testId="chart-landing-source">
            <LandingSourceBar data={data.landing.by_source} />
          </ChartCard>
        )}
      </div>
      <RedemptionReport data={data} exportUrl={exportUrl} contestId={contestId} />
    </>
  );
};

const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const RedemptionReport = ({ data, exportUrl, contestId }) => (
  <section className="mt-8" data-testid="redemption-report">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <h2 className="font-display font-bold text-lg flex items-center gap-2">
        <PackageCheck className="h-5 w-5 text-emerald-400" /> Report Ritiri Premi
      </h2>
      <div className="flex gap-2">
        {contestId && <RemindersButton contestId={contestId} />}
        {exportUrl && (
          <a href={exportUrl} data-testid="export-redemptions-button">
            <Button variant="secondary" size="sm" className="gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Excel Ritiri
            </Button>
          </a>
        )}
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3 mb-5">
      <KpiCard label="Ritirati" value={data.redeemed_total} testId="kpi-redeemed" />
      <KpiCard label="In sospeso" value={data.pending_total} accent testId="kpi-pending" />
      <KpiCard label="Scaduti" value={data.expired_total} testId="kpi-expired" />
    </div>
    <div className="grid lg:grid-cols-2 gap-5">
      <ChartCard title="Ritiri per punto vendita" subtitle="Premi consegnati per sede di ritiro" testId="chart-redeem-store">
        {data.by_redeem_store?.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_redeem_store.slice(0, 10)} layout="vertical" margin={MARGIN_V}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="label" stroke="#64748b" fontSize={11} width={130} />
              <Tooltip {...tooltipStyle} cursor={CURSOR} />
              <Bar dataKey="redeemed" name="Ritirati" fill="#059669" radius={3} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Empty />
        )}
      </ChartCard>
      <Card className="p-5 bg-card border-border" data-testid="pending-table">
        <h3 className="font-display font-bold text-base">Premi in sospeso</h3>
        <p className="text-xs text-muted-foreground mb-3">Vincite non ancora ritirate ({data.pending_total})</p>
        <div className="max-h-64 overflow-y-auto">
          {data.pending?.length ? (
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground sticky top-0 bg-card">
                <tr>
                  <th className="py-1.5 pr-2 font-semibold">Seriale</th>
                  <th className="py-1.5 pr-2 font-semibold">Premio</th>
                  <th className="py-1.5 pr-2 font-semibold">Vinto il</th>
                  <th className="py-1.5 font-semibold">Scade</th>
                </tr>
              </thead>
              <tbody>
                {data.pending.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`pending-row-${p.id}`}>
                    <td className="py-1.5 pr-2 font-mono-code">{p.receipt_serial}</td>
                    <td className="py-1.5 pr-2 truncate max-w-[140px]">{p.prize_name}</td>
                    <td className="py-1.5 pr-2 text-muted-foreground whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    <td className="py-1.5 whitespace-nowrap">{p.expires_at ? fmtDate(p.expires_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="h-40 grid place-items-center text-sm text-muted-foreground">Nessun premio in sospeso</div>
          )}
        </div>
      </Card>
    </div>
  </section>
);
